import React, { createContext, useContext, useState, useEffect } from 'react';

const LocationSearchContext = createContext();


export const LocationProvider = ({ children }) => {
  const [locationSearchValue, setLocationSearchValue] = useState("");
  const [destinationSelected, setDestinationSelected] = useState(false);
  const [searchCoordinates, setSearchCoordinates] = useState({ lat: null, lng: null });
  const [mapBounds, setMapBounds] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [resetMap, setResetMap] = useState(false);

  const initialMapBounds = {
    "northEast": {lat: 45.81457370563775, lng: -73.6386277125},
    "southWest": {lat: 33.2308029302877, lng: -123.4726120875}
  };

  // Initialize state from local storage
  useEffect(() => {
    const storedLocationSearchValue = sessionStorage.getItem('locationSearchValue');
    const storedDestinationSelected = sessionStorage.getItem('destinationSelected');
    const storedSearchCoordinates = sessionStorage.getItem('searchCoordinates');
    const storedMapBounds = sessionStorage.getItem('mapBounds');

    if (storedLocationSearchValue) {
      // PP-371 — locationSearchValue holds a plain string while the header
      // search box is being typed into, but a Google Places address object
      // ({street1, city, state, zip, description}) once a prediction is
      // selected (see SearchLocations.js/NewPropertyLocations.js). The write
      // side below persists it via JSON.stringify, so mirror that here
      // instead of using the raw stored string as-is — otherwise an object
      // value round-trips through sessionStorage as the literal string
      // "[object Object]" on the next full page load, silently losing
      // city/state and breaking the server's location-search match.
      try {
        setLocationSearchValue(JSON.parse(storedLocationSearchValue));
      } catch (error) {
        // Pre-fix sessions (or a plain string search-in-progress value) have
        // a raw, non-JSON string persisted directly — use it as-is rather
        // than losing the value entirely.
        setLocationSearchValue(storedLocationSearchValue);
      }
    }

    try {
      if (storedDestinationSelected) {
        setDestinationSelected(JSON.parse(storedDestinationSelected));
      }

      if (storedSearchCoordinates) {
        setSearchCoordinates(JSON.parse(storedSearchCoordinates));
      }

      if (storedMapBounds) {
        setMapBounds(JSON.parse(storedMapBounds));
      }
    } catch (error) {
      console.error('Error parsing stored data:', error);
    }

  }, []);

  // Update local storage whenever state changes
  useEffect(() => {
    // PP-371 — JSON.stringify (not a raw set) so an object value (a
    // selected Places prediction) round-trips correctly instead of
    // coercing to the literal string "[object Object]" on the next reload.
    sessionStorage.setItem('locationSearchValue', JSON.stringify(locationSearchValue));
  }, [locationSearchValue]);

  useEffect(() => {
    sessionStorage.setItem('destinationSelected', JSON.stringify(destinationSelected));
  }, [destinationSelected]);

  useEffect(() => {
    sessionStorage.setItem('searchCoordinates', JSON.stringify(searchCoordinates));
  }, [searchCoordinates]);

  useEffect(() => {
    sessionStorage.setItem('mapBounds', JSON.stringify(mapBounds));
  }, [mapBounds]);

  const clearLocationSearch = () => {
    setLocationSearchValue("");
    setSearchCoordinates({ lat: null, lng: null });
    setMapBounds(initialMapBounds);
    setDestinationSelected(false);
    setResetMap(true);
    setTimeout(() => {
      setResetMap(false);
    }, 100);
  };

  return (
    <LocationSearchContext.Provider value={{
      locationSearchValue,
      setLocationSearchValue,
      destinationSelected,
      setDestinationSelected,
      searchCoordinates,
      setSearchCoordinates,
      mapBounds,
      setMapBounds,
      isLoading,
      setIsLoading,
      clearLocationSearch,
      resetMap
    }}>
      {children}
    </LocationSearchContext.Provider>
  );
};

export const useLocationContext = () => useContext(LocationSearchContext);
